import { ChangeDetectionStrategy, Component, input, model } from '@angular/core'
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { InfoCardComponent } from './info-card.component'

let nextId = 0

/**
 * Aufklappbarer Hintergrund-Erklaertext in der getoenten Info-Karte.
 *
 * Das App-Idiom fuer erklaerungsbeduerftige Stellen: **Wesentliches bleibt sichtbar,
 * Hintergrundwissen klappt auf.** Zugeklappt steht nur eine Zeile da (Icon + Frage/Titel +
 * Chevron) — wer die Antwort braucht, klickt; alle anderen werden nicht zugetextet. Erste
 * Auspraegung war der SEPA-Mandats-Dialog ("Ab wann das Mandat gilt").
 *
 * ```html
 * <lib-expandable-info icon="help" heading="Warum zwei Einreichungen pro Monat?">
 *   <p>Kurzer Satz. Noch ein kurzer Satz.</p>
 * </lib-expandable-info>
 * ```
 *
 * Der Inhalt wird projiziert und bleibt im DOM (Hoehen-Animation ueber
 * `grid-template-rows`), damit Screenreader-Fokus und Ctrl+F den Text finden.
 */
@Component({
  selector: 'lib-expandable-info',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule, MatIconModule, InfoCardComponent],
  template: `
    <lib-info-card [appearance]="appearance()">
      <mat-card>
        <mat-card-content class="info-card-content">
          <mat-icon>{{ icon() }}</mat-icon>
          <div class="info-text">
            <button
              type="button"
              class="info-toggle"
              (click)="expanded.set(!expanded())"
              [attr.aria-expanded]="expanded()"
              [attr.aria-controls]="contentId"
            >
              <span class="mat-title-medium">{{ heading() }}</span>
              <mat-icon class="chevron" [class.open]="expanded()">expand_more</mat-icon>
            </button>
            <div class="collapsible" [class.open]="expanded()">
              <div class="collapsible-inner">
                <div [id]="contentId" class="mat-body-medium explanation">
                  <ng-content />
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </lib-info-card>
  `,
  styles: `
    :host {
      display: block;
    }

    .info-card-content {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .info-card-content > mat-icon {
      color: var(--mat-sys-primary);
    }

    .info-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
    }

    .info-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      width: 100%;
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
      transition: color 0.15s ease;

      &:hover {
        color: var(--mat-sys-primary);
      }

      .chevron {
        flex-shrink: 0;
        transition: transform 0.2s ease;

        &.open {
          transform: rotate(180deg);
        }
      }
    }

    /* Weiche Hoehen-Animation, ohne den Inhalt aus dem DOM zu nehmen. */
    .collapsible {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.2s ease;

      &.open {
        grid-template-rows: 1fr;
      }
    }

    .collapsible-inner {
      overflow: hidden;
    }

    .explanation {
      padding-top: 0.35rem;
      color: var(--mat-sys-on-surface-variant);
    }

    @media (prefers-reduced-motion: reduce) {
      .info-toggle,
      .info-toggle .chevron,
      .collapsible {
        transition: none;
      }
    }
  `,
})
export class ExpandableInfoComponent {
  /** Zeile, die zugeklappt sichtbar bleibt. Als Frage formulieren, wenn es eine beantwortet. */
  readonly heading = input.required<string>()
  readonly icon = input<string>('info')
  readonly appearance = input<'info' | 'error' | 'warn' | 'neutral'>('info')

  /** Aufklappzustand — `model`, damit Aufrufer ihn vorbelegen oder mitlesen koennen. */
  readonly expanded = model(false)

  protected readonly contentId = `lib-expandable-info-${nextId++}`
}
